import * as React from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TablePagination,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableCell,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { useState, useEffect } from "react";
import Config from "../config.json";

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#2e2e2e",
    color: "#ffffff",
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "rgba(46, 46, 46, 0.04)",
  },
}));

// Custom hook for announcement data
function useAnnouncement(url) {
  const [data, setData] = useState([]);
  useEffect(() => {
    if (!url) return;
    fetch(url)
      .then((res) => res.json())
      .then((res) => setData(res.data || []))
      .catch((err) => console.error("Fetch error:", err));
  }, [url]);
  return data;
}

// Project type configurations
const projectConfigs = {
  urban: { label: "Urban Base Maps", path: "urban", color: "#1976d2", bgColor: "rgba(25, 118, 210, 0.1)" },
  nonurban: { label: "Non-Urban Base Maps", path: "nonurban", color: "#388e3c", bgColor: "rgba(56, 142, 60, 0.1)" },
  qcqa: { label: "QC/QA Base Maps", path: "qc", color: "#f57c00", bgColor: "rgba(245, 124, 0, 0.1)" },
  komputer: { label: "Paket Pengadaan Laptop Media Pembelajaran", path: "pengadaan-laptop-media-pembelajaran", color: "#1976d2", bgColor: "rgba(25, 118, 210, 0.1)" },
  pmc: { label: "PMC", path: "pmc", color: "#7b1fa2", bgColor: "rgba(123, 31, 162, 0.1)" },
  generalisasi: { label: "Generalisasi", path: "generalisasi", color: "#00796b", bgColor: "rgba(0, 121, 107, 0.1)" },
  cons: { label: "Procurement Specialist (5.2)", path: "cps", color: "#d32f2f", bgColor: "rgba(211, 47, 47, 0.1)" },
  climate: { label: "Climate Change Policy Expert", path: "Climate-Change", color: "#2e7d32", bgColor: "rgba(46, 125, 50, 0.1)" },
  perpus: { label: "Koleksi Perpustakaan", path: "penambahan-koleksi-perpustakaan", color: "#795548", bgColor: "rgba(121, 85, 72, 0.1)" },
  perpus1: { label: "Koleksi Perpustakaan (ULANG)", path: "penambahan-koleksi-perpustakaan", color: "#795548", bgColor: "rgba(121, 85, 72, 0.1)" },
  perpus2: { label: "Koleksi Perpustakaan (ULANG 2)", path: "penambahan-koleksi-perpustakaan", color: "#795548", bgColor: "rgba(121, 85, 72, 0.1)" },
  perpus3: { label: "Koleksi Perpustakaan (ULANG 3)", path: "penambahan-koleksi-perpustakaan", color: "#795548", bgColor: "rgba(21, 101, 192, 0.1)" },
  geo: { label: "Geospatial Data Expert", path: "GeoDataUtilization", color: "#1565c0", bgColor: "rgba(21, 101, 192, 0.1)" },
  pengadaan: { label: "Procurement Specialist", path: "procurement-specialist", color: "#e65100", bgColor: "rgba(230, 81, 0, 0.1)" },
  risiko: { label: "Risk Management", path: "risk-management", color: "#c62828", bgColor: "rgba(198, 40, 40, 0.1)" },
  proyek: { label: "Project Management", path: "project-management", color: "#6a1b9a", bgColor: "rgba(106, 27, 154, 0.1)" },
  proyek1: { label: "Project Management (UPDATED)", path: "project-management", color: "#6a1b9a", bgColor: "rgba(106, 27, 154, 0.1)" },
  enhancement: { label: "Geospatial Capacity Enhancement", path: "capacity-enhancement", color: "#4527a0", bgColor: "rgba(69, 39, 160, 0.1)" },
  multimedia: { label: "PC, Laptop & Multimedia", path: "pengadaan-multimedia", color: "#ff6f00", bgColor: "rgba(255, 111, 0, 0.1)" },
  sarana: { label: "Peningkatan Sarana", path: "peningkatan-sarana", color: "#bf360c", bgColor: "rgba(191, 54, 12, 0.1)" },
  multimedia1: { label: "PC, Laptop & Multimedia (ULANG)", path: "pengadaan-multimedia", color: "#ff6f00", bgColor: "rgba(255, 111, 0, 0.1)" },
  sarana1: { label: "Peningkatan Sarana (ULANG)", path: "peningkatan-sarana", color: "#bf360c", bgColor: "rgba(191, 54, 12, 0.1)" },
  sarana2: { label: "Peningkatan Sarana (ULANG 2)", path: "peningkatan-sarana", color: "#bf360c", bgColor: "rgba(191, 54, 12, 0.1)" },
  geoclimate: { label: "Geospatial Climate Platform", path: "geospatial-platform-for-climate-action", color: "#1b5e20", bgColor: "rgba(27, 94, 32, 0.1)" },
  devimaps: { label: "Integrated Map Production", path: "development-of-integrated-map-production-system", color: "#283593", bgColor: "rgba(40, 53, 147, 0.1)" },
};

// Unified Announcement Table
const UnifiedAnnouncementTable = ({ allData, selectedFilter }) => {
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;
  const handleChangePage = (_, newPage) => setPage(newPage);

  const filteredData =
    selectedFilter === "all"
      ? allData
      : allData.filter((item) => item.projectType === selectedFilter);

  const sortedData = [...filteredData].sort(
    (a, b) => new Date(b.time_uploaded) - new Date(a.time_uploaded)
  );

  const slicedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    setPage(0);
  }, [selectedFilter]);

  const baseUrl = "https://ilaspp-proc.big.go.id/#/";

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getFilteredProjectCount = () => {
    if (selectedFilter === "all") {
      const uniqueProjects = [...new Set(allData.map((item) => item.projectType))];
      return uniqueProjects.length;
    }
    return 1;
  };

  return (
    <Paper sx={{ borderRadius: "12px", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Project Name</StyledTableCell>
              <StyledTableCell>Announcement</StyledTableCell>
              <StyledTableCell align="center">Date</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {slicedData.length > 0 ? (
              slicedData.map((row) => {
                const config = projectConfigs[row.projectType];
                return (
                  <StyledTableRow key={`${row.projectType}-${row.public_id}`}>
                    <StyledTableCell sx={{ width: "220px" }}>
                      <Chip
                        label={config.label}
                        size="small"
                        sx={{
                          backgroundColor: config.bgColor,
                          color: config.color,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          border: `1px solid ${config.color}`,
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      <a
                        href={`${baseUrl}${config.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: config.color,
                          textDecoration: "none",
                          fontWeight: 500,
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = "underline";
                          e.target.style.opacity = "0.8";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = "none";
                          e.target.style.opacity = "1";
                        }}
                      >
                        {row.title}
                      </a>
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: "120px" }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", fontWeight: 500 }}
                      >
                        {formatDate(row.time_uploaded)}
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <StyledTableRow>
                <StyledTableCell align="center" colSpan={3}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#666",
                      fontStyle: "italic",
                      padding: "20px",
                    }}
                  >
                    {selectedFilter === "all"
                      ? "No Announcements Found"
                      : selectedFilter === "qcqa"
                      ? "The work package has not started yet. Project activities and progress updates will be provided once implementation begins."
                      : `No Announcements Found for ${projectConfigs[selectedFilter]?.label}`}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[]}
        sx={{
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          "& .MuiTablePagination-toolbar": {
            paddingLeft: "16px",
            paddingRight: "8px",
          },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "0.875rem",
            color: "#666",
          },
        }}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count} announcements ${
            selectedFilter !== "all"
              ? `from ${projectConfigs[selectedFilter]?.label}`
              : `from ${getFilteredProjectCount()} projects`
          }`
        }
      />
    </Paper>
  );
};

// Main Component
export default function DashboardPage() {
  const [selectedFilter, setSelectedFilter] = useState("all");

  // Dialog: Technical Bid Opening
  const [openDialog, setOpenDialog] = React.useState(false);
  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);

  // Dialog: Non-Urban Financial
  const [openDialogFinancial, setOpenDialogFinancial] = React.useState(false);
  const handleOpenDialogFinancial = () => setOpenDialogFinancial(true);
  const handleCloseDialogFinancial = () => setOpenDialogFinancial(false);

  // Dialog: Urban Financial
  const [openDialogFinancialUrban, setOpenDialogFinancialUrban] = React.useState(false);
  const handleOpenDialogFinancialUrban = () => setOpenDialogFinancialUrban(true);
  const handleCloseDialogFinancialUrban = () => setOpenDialogFinancialUrban(false);

  // Dialog: PMC
  const [openDialogPMC, setOpenDialogPMC] = React.useState(false);
  const handleOpenDialogPMC = () => setOpenDialogPMC(true);
  const handleCloseDialogPMC = () => setOpenDialogPMC(false);

  // Dialog: QC/QA Tech Proposal (NEW)
  const [openDialogQCQA, setOpenDialogQCQA] = React.useState(false);
  const handleOpenDialogQCQA = () => setOpenDialogQCQA(true);
  const handleCloseDialogQCQA = () => setOpenDialogQCQA(false);

  // Dialog: Non-Urban Notification
  const [openDialogNotif, setOpenDialogNotif] = React.useState(false);
  const handleOpenDialogNotif = () => setOpenDialogNotif(true);
  const handleCloseDialogNotif = () => setOpenDialogNotif(false);

  // Dialog: Urban Notification
  const [openDialogNotifUrban, setOpenDialogNotifUrban] = React.useState(false);
  const handleOpenDialogNotifUrban = () => setOpenDialogNotifUrban(true);
  const handleCloseDialogNotifUrban = () => setOpenDialogNotifUrban(false);

  const announcementUrban = useAnnouncement(Config.api_domain + "/pengumuman/active/");
  const announcementNonUrban = useAnnouncement(Config.api_domain + "/berkas/active/");
  const announcementKomputer = useAnnouncement(Config.api_domain + "/komputer/active/");
  const announcementQCQA = useAnnouncement(Config.api_domain + "/qcqa/active/");
  const announcementPMC = useAnnouncement(Config.api_domain + "/pmc/active/");
  const announcementGeneralisasi = useAnnouncement(Config.api_domain + "/generalisasi/active/");
  const announcementCONS = useAnnouncement(Config.api_domain + "/cons/active/");
  const announcementClimate = useAnnouncement(Config.api_domain + "/climate/active/");
  const announcementGeo = useAnnouncement(Config.api_domain + "/geo/active/");
  const announcementPengadaan = useAnnouncement(Config.api_domain + "/pengadaan/active/");
  const announcementRisiko = useAnnouncement(Config.api_domain + "/risiko/active/");
  const announcementProyek = useAnnouncement(Config.api_domain + "/proyek/active/");
  const announcementProyek1 = useAnnouncement(Config.api_domain + "/proyek1/active/");
  const announcementEnhancement = useAnnouncement(Config.api_domain + "/enhancement/active/");
  const announcementMultimedia = useAnnouncement(Config.api_domain + "/multimedia/active/");
  const announcementSarana = useAnnouncement(Config.api_domain + "/sarana/active/");
  const announcementMultimedia1 = useAnnouncement(Config.api_domain + "/multimedia1/active/");
  const announcementPerpus = useAnnouncement(Config.api_domain + "/perpus/active/");
  const announcementPerpus1 = useAnnouncement(Config.api_domain + "/perpus1/active/");
  const announcementPerpus2 = useAnnouncement(Config.api_domain + "/perpus2/active/");
  const announcementPerpus3 = useAnnouncement(Config.api_domain + "/perpus3/active/");
  const announcementSarana1 = useAnnouncement(Config.api_domain + "/sarana1/active/");
  const announcementSarana2 = useAnnouncement(Config.api_domain + "/sarana2/active/");
  const announcementGeoClimate = useAnnouncement(Config.api_domain + "/geoclimate/active/");
  const announcementDevimaps = useAnnouncement(Config.api_domain + "/devimaps/active/");

  const getAllAnnouncementsData = () => {
    return [
      ...announcementUrban.map((d) => ({ ...d, projectType: "urban", download_path: "pengumuman/download" })),
      ...announcementNonUrban.map((d) => ({ ...d, projectType: "nonurban", download_path: "berkas/download" })),
      ...announcementQCQA.map((d) => ({ ...d, projectType: "qcqa", download_path: "qcqa/download" })),
      ...announcementPMC.map((d) => ({ ...d, projectType: "pmc", download_path: "pmc/download" })),
      ...announcementPerpus.map((d) => ({ ...d, projectType: "perpus", download_path: "penambahan-koleksi-perpustakaan/download" })),
      ...announcementPerpus1.map((d) => ({ ...d, projectType: "perpus1", download_path: "penambahan-koleksi-perpustakaan/download" })),
      ...announcementPerpus2.map((d) => ({ ...d, projectType: "perpus2", download_path: "penambahan-koleksi-perpustakaan/download" })),
      ...announcementPerpus3.map((d) => ({ ...d, projectType: "perpus3", download_path: "penambahan-koleksi-perpustakaan/download" })),
      ...announcementKomputer.map((d) => ({ ...d, projectType: "komputer", download_path: "pengadaan-laptop-media-pembelajaran/download" })),
      ...announcementGeneralisasi.map((d) => ({ ...d, projectType: "generalisasi", download_path: "generalisasi/download" })),
      ...announcementCONS.map((d) => ({ ...d, projectType: "cons", download_path: "cons/download" })),
      ...announcementClimate.map((d) => ({ ...d, projectType: "climate", download_path: "climate/download" })),
      ...announcementGeo.map((d) => ({ ...d, projectType: "geo", download_path: "geo/download" })),
      ...announcementPengadaan.map((d) => ({ ...d, projectType: "pengadaan", download_path: "pengadaan/download" })),
      ...announcementRisiko.map((d) => ({ ...d, projectType: "risiko", download_path: "risiko/download" })),
      ...announcementProyek.map((d) => ({ ...d, projectType: "proyek", download_path: "proyek/download" })),
      ...announcementProyek1.map((d) => ({ ...d, projectType: "proyek1", download_path: "proyek/download" })),
      ...announcementEnhancement.map((d) => ({ ...d, projectType: "enhancement", download_path: "enhancement/download" })),
      ...announcementMultimedia.map((d) => ({ ...d, projectType: "multimedia", download_path: "multimedia/download" })),
      ...announcementSarana.map((d) => ({ ...d, projectType: "sarana", download_path: "sarana/download" })),
      ...announcementMultimedia1.map((d) => ({ ...d, projectType: "multimedia1", download_path: "multimedia/download" })),
      ...announcementSarana1.map((d) => ({ ...d, projectType: "sarana1", download_path: "sarana1/download" })),
      ...announcementSarana2.map((d) => ({ ...d, projectType: "sarana2", download_path: "sarana2/download" })),
      ...announcementGeoClimate.map((d) => ({ ...d, projectType: "geoclimate", download_path: "geoclimate/download" })),
      ...announcementDevimaps.map((d) => ({ ...d, projectType: "devimaps", download_path: "devimaps/download" })),
    ];
  };

  const allAnnouncementsData = getAllAnnouncementsData();

  return (
    <Box padding={2}>
      <Box
        display="flex"
        width="100%"
        gap={2}
        flexDirection={{ xs: "column", md: "row" }}
      >
        {/* Left side: Description */}
        <Box flex={{ xs: "1", md: "0 0 60%" }}>
          <Typography
            variant="h3"
            component="h3"
            gutterBottom
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              mb: 2,
              color: "#2e2e2e",
            }}
          >
            ILASPP PROCUREMENT
          </Typography>
          <Box sx={{ width: "95%" }}>
            <Typography
              variant="body1"
              paragraph
              align="left"
              sx={{ lineHeight: 1.6, color: "#333" }}
            >
              To support the implementation of ILASPP objectives, ILASPP
              Procurement has been established to accommodate the production of
              large-scale geospatial data. This system ensures transparency,
              competitiveness, and efficiency in the tendering process for
              mapping services. It is designed to inform qualified vendors with
              the technical capacity to deliver high-resolution base maps at
              multiple scales—ranging from 1:5,000 to 1:1,000,000—covering
              approximately 82,497 square kilometers. The procurement mechanism
              aligns with international standards and is structured to
              facilitate timely execution from 2025 to 2027, while ensuring
              quality and compliance with climate-resilient spatial planning
              objectives.
            </Typography>
          </Box>

          <Typography
            variant="h6"
            sx={{ mt: 3, mb: 1, fontWeight: "bold", color: "#2e2e2e" }}
          >
            BID OPENING ANNOUNCEMENT
          </Typography>

          <Paper sx={{ borderRadius: "12px", maxWidth: "95%", overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell sx={{ width: "60px" }}>No</StyledTableCell>
                    <StyledTableCell>Announcement</StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: "140px" }}>
                      Event Date
                    </StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: "120px" }}>
                      Action
                    </StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>


                  {/* Row 2 - Non-Urban Financial Parts */}
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="1" size="small" sx={{ backgroundColor: "rgba(56, 142, 60, 0.1)", color: "#388e3c", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #388e3c", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        Public Opening of Financial Parts for The Provision of Basic Geospatial Data And Base Maps of Non-Urban Areas Of Indonesia
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        20 May 2026, 13:00 (GMT+7)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialogFinancial}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#388e3c", "&:hover": { backgroundColor: "#2e7d32" }, boxShadow: "0 2px 4px rgba(56, 142, 60, 0.25)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>

{/* Row 1 - Urban Financial Parts */}
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="2" size="small" sx={{ backgroundColor: "rgba(25, 118, 210, 0.1)", color: "#1976d2", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #1976d2", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        Public Opening of Financial Parts for The Provision of Basic Geospatial Data And Base Maps of Urban Areas of Indonesia
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        13 May 2026, 13:00 (GMT+7)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialogFinancialUrban}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#1565c0" }, boxShadow: "0 2px 4px rgba(25, 118, 210, 0.25)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                  {/* Row 3 - QC/QA Tech Proposal (NEW) */}
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="3" size="small" sx={{ backgroundColor: "rgba(245, 124, 0, 0.1)", color: "#f57c00", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #f57c00", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        Opening of Tech Proposal: Quality Assurance and Quality Control Consultancy for the Provision of Base Maps of Indonesia
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        27 April 2026, 13:00 (GMT+7)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialogQCQA}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#f57c00", "&:hover": { backgroundColor: "#e65100" }, boxShadow: "0 2px 4px rgba(245, 124, 0, 0.25)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>

                  {/* Row 4 - PMC */}
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="4" size="small" sx={{ backgroundColor: "rgba(123, 31, 162, 0.1)", color: "#7b1fa2", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #7b1fa2", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        Opening of Tech Proposal: Support Management of Integrated Land Administration And Spatial Planning (ILASP) Activities – (Project Management Consultant / PMC)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        3 February 2026, 14:00 (GMT+7)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialogPMC}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#7b1fa2", "&:hover": { backgroundColor: "#6a1b9a" }, boxShadow: "0 2px 4px rgba(123, 31, 162, 0.25)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>

                  {/* Row 5 - Technical Parts Bid Opening */}
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="5" size="small" sx={{ backgroundColor: "rgba(25, 118, 210, 0.1)", color: "#1976d2", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #1976d2", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        Technical Parts Bid Opening for The Provision of Basic Geospatial Data And Base Maps of Urban and Non-Urban Areas Of Indonesia
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        21 October 2025, 13:00 (GMT+7)
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialog}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#1565c0" }, boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>

                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* === NOTIFICATION ALERTS TABLE === */}
          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: "bold", color: "#2e2e2e" }}>
            NOTIFICATION ALERTS
          </Typography>
          <Paper sx={{ borderRadius: "12px", maxWidth: "95%", overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell sx={{ width: "60px" }}>No</StyledTableCell>
                    <StyledTableCell>Notification</StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: "140px" }}>Date</StyledTableCell>
                    <StyledTableCell align="center" sx={{ width: "120px" }}>Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="1" size="small" sx={{ backgroundColor: "rgba(25, 118, 210, 0.1)", color: "#1976d2", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #1976d2", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        (Urban Project) Notification of Evaluation of Technical Parts
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        April 10, 2026
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialogNotifUrban}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#1565c0" }, boxShadow: "0 2px 4px rgba(25, 118, 210, 0.25)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell>
                      <Chip label="2" size="small" sx={{ backgroundColor: "rgba(56, 142, 60, 0.1)", color: "#388e3c", fontWeight: 600, fontSize: "0.75rem", border: "1px solid #388e3c", minWidth: "32px" }} />
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#333" }}>
                        (Non-Urban Project) Notification of Evaluation of Technical Parts
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Typography variant="caption" sx={{ color: "#666", fontWeight: 500 }}>
                        March 26, 2026
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button variant="contained" size="small" onClick={handleOpenDialogNotif}
                        sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, backgroundColor: "#388e3c", "&:hover": { backgroundColor: "#2e7d32" }, boxShadow: "0 2px 4px rgba(56, 142, 60, 0.25)" }}>
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* === DIALOGS === */}

          {/* Dialog: Urban Financial Parts */}
          <Dialog
  open={openDialogFinancialUrban}
  onClose={handleCloseDialogFinancialUrban}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    Announcement – Public Opening of Financial Parts (Urban)
  </DialogTitle>

  <DialogContent dividers>
    <DialogContentText
      component="div"
      sx={{
        whiteSpace: "pre-line",
        fontSize: "0.9rem",
        color: "#333",
        lineHeight: 1.8,
      }}
    >
      <>
        Please be advised that the Public Opening of Financial Parts for the contract titled{" "}
        <strong>
          Procurement of The Provision of Basic Geospatial Data and Base Maps of Urban Areas of Indonesia
        </strong>{" "}
        (RFB No: ID-BIG-482852-NC-RFB) will be conducted as follows:

        <br /><br />

        <strong>Date:</strong> May 13, 2026
        <br />

        <strong>Time:</strong> 01.00 P.M (Western Indonesia Time)
        <br />

        <strong>Offline Venue:</strong> Auditorium Bhumandala (Gedung M) Badan Informasi Geospasial, Jl. Raya Jakarta-Bogor Km. 46 Cibinong, Bogor, Indonesia
        <br />

        <strong>Online Platform:</strong>{" "}
        <a
          href="https://big-go-id.zoom.us/j/91522924287?pwd=82jr67vs4w9EtKFDdbHh5sedk6qoSN.1"
          target="_blank"
          rel="noopener noreferrer"
        >
          Zoom Meeting Link
        </a>

        <br /><br />

        <strong>Meeting ID:</strong> 915 2292 4287
        <br />

        <strong>Passcode:</strong> nx9^zd7J

        <br /><br />

        <strong>Meeting Chat Link:</strong>{" "}
        <a
          href="https://big-go-id.zoom.us/launch/jc/91522924287"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Chat
        </a>

        <br /><br />

        Thank you for your attention.
      </>
    </DialogContentText>
  </DialogContent>

  <DialogActions>
    <Button onClick={handleCloseDialogFinancialUrban}>
      Close
    </Button>
  </DialogActions>
</Dialog>

          {/* Dialog: Non-Urban Financial Parts */}
          <Dialog open={openDialogFinancial} onClose={handleCloseDialogFinancial} maxWidth="sm" fullWidth>
  <DialogTitle>Announcement – Rescheduling of Public Opening of Financial Parts</DialogTitle>
  
  <DialogContent dividers>
    <DialogContentText sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", color: "#333" }}>
      Please be advised that the schedule for the Public Opening of Financial Parts for the contract titled Procurement of The Provision of Basic Geospatial Data and Base Maps of Non-Urban Areas of Indonesia (RFB No: ID-BIG-487988-NC-RFB) has been revised.
      {"\n\n"}
      The revision is made to accommodate the additional time required for the World Bank to issue a “No Objection” regarding the complaint resolution process. Therefore, the opening originally scheduled for{" "}
      <span style={{ textDecoration: "underline", textDecorationColor: "red" }}>
        May 13, 2026
      </span>{" "}
      has been rescheduled as follows:
      {"\n\n"}
      <strong>Date:</strong>{" "}
      <span style={{ textDecoration: "underline", textDecorationColor: "red" }}>
        May 20, 2026
      </span>
      {"\n\n"}
      <strong>Time:</strong> 01.00 P.M (Western Indonesia Time)
      {"\n\n"}
      <strong>Venue / Platform:</strong> To be confirmed
      {"\n\n"}
      Detailed information regarding the venue and/or meeting link will be shared in due course.
      {"\n"}
      Thank you for your attention.
    </DialogContentText>
  </DialogContent>

  <DialogActions>
    <Button onClick={handleCloseDialogFinancial}>Close</Button>
  </DialogActions>
</Dialog>

          {/* Dialog: QC/QA Tech Proposal (NEW) */}
          <Dialog open={openDialogQCQA} onClose={handleCloseDialogQCQA} maxWidth="sm" fullWidth>
            <DialogTitle>Opening of Tech Proposal – QC/QA (Zoom Meeting)</DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", color: "#333" }}>
                ILASP PHLN invites you to join a scheduled Zoom meeting.
                {"\n\n"}<strong>Topic:</strong> Opening of Tech Proposal: Quality Assurance and Quality Control Consultancy for the Provision of Base Maps of Indonesia
                {"\n\n"}<strong>Time:</strong> Apr 27, 2026, 01:00 PM Jakarta
                {"\n\n"}<strong>Join Zoom Meeting:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/j/94630805104?pwd=RDKRosA7YwAmacQbBf4XOR6eMbxvKl.1"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#f57c00", textDecoration: "underline" }}
                >
                  https://big-go-id.zoom.us/j/94630805104?pwd=RDKRosA7YwAmacQbBf4XOR6eMbxvKl.1
                </a>
                {"\n\n"}<strong>Meeting ID:</strong> 946 3080 5104
                {"\n"}<strong>Passcode:</strong> =y5^uuyj
                {"\n\n"}<strong>Join via SIP:</strong> 94630805104@zoomcrc.com
                {"\n\n"}<strong>Join instructions:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/meetings/94630805104/invitations?signature=_4ODCapXfLvM9nN5MoSByY5WHOZjOGuPlg6sxlYLrjs"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#f57c00", textDecoration: "underline" }}
                >
                  https://big-go-id.zoom.us/meetings/94630805104/invitations
                </a>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialogQCQA}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog: PMC */}
          <Dialog open={openDialogPMC} onClose={handleCloseDialogPMC} maxWidth="sm" fullWidth>
            <DialogTitle>Opening of Tech Proposal – PMC (Zoom Meeting)</DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", color: "#333" }}>
                ILASP PHLN invites you to join a scheduled Zoom meeting.
                {"\n\n"}<strong>Topic:</strong> Opening of Tech Proposal: Support Management of Integrated Land Administration And Spatial Planning (ILASP) Activities – (Project Management Consultant / PMC)
                {"\n\n"}<strong>Time:</strong> Feb 3, 2026, 02:00 PM Jakarta
                {"\n\n"}<strong>Join Zoom Meeting:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/j/94918071148?pwd=vZNCg5T5SE2FRo3GXgnks5WGFL2eOe.1"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#7b1fa2", textDecoration: "underline" }}
                >
                  https://big-go-id.zoom.us/j/94918071148?pwd=vZNCg5T5SE2FRo3GXgnks5WGFL2eOe.1
                </a>
                {"\n\n"}<strong>Meeting ID:</strong> 949 1807 1148
                {"\n"}<strong>Passcode:</strong> 7pknj8^+
                {"\n\n"}<strong>Join instructions:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/meetings/94918071148/invitations?signature=NTwh94H6Sq3QgXbiOz5gvWnDv6wHozxiaGjxLkt_Eo0"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#7b1fa2", textDecoration: "underline" }}
                >
                  https://big-go-id.zoom.us/meetings/94918071148/invitations
                </a>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialogPMC}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog: Non-Urban Notification */}
          <Dialog open={openDialogNotif} onClose={handleCloseDialogNotif} maxWidth="sm" fullWidth>
            <DialogTitle>Notification of Evaluation of Technical Parts</DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", color: "#333" }}>
                <strong>Publication Date:</strong> March 26, 2026
                {"\n\n"}The Notification of Evaluation of Technical Parts and Public Opening of Financial Parts for the procurement of the Provision of Basic Geospatial Data and Base Maps of Non-Urban Areas of Indonesia (RFB No: ID-BIG-487988-NC-RFB) has been sent to all bidders via the email addresses provided in their Bidding Documents. Please check your email for further details.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialogNotif}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog: Urban Notification */}
          <Dialog open={openDialogNotifUrban} onClose={handleCloseDialogNotifUrban} maxWidth="sm" fullWidth>
            <DialogTitle>Notification of Evaluation of Technical Parts</DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", color: "#333" }}>
                <strong>Publication Date:</strong> April 10, 2026
                {"\n\n"}The Notification of Evaluation of Technical Parts and Public Opening of Financial Parts for the procurement of the Provision of Basic Geospatial Data and Base Maps of Urban Areas of Indonesia (RFB No: ID-BIG-482852-NC-RFB) has been sent to all bidders via the email addresses provided in their Bidding Documents. Please check your email for further details.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialogNotifUrban}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Dialog: Technical Bid Opening */}
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Technical Parts Bid Opening - Zoom Meeting</DialogTitle>
            <DialogContent dividers>
              <DialogContentText sx={{ whiteSpace: "pre-line", fontSize: "0.9rem", color: "#333" }}>
                Procurement Working Group ILASPP DPRWD is inviting you to a scheduled Zoom meeting.
                {"\n\n"}<strong>Topic:</strong> Technical Parts Bid Opening for The Provision of Basic Geospatial Data And Base Maps of Urban and Non-Urban Areas Of Indonesia
                {"\n\n"}<strong>Time:</strong> Oct 21, 2025 01:00 PM Jakarta
                {"\n\n"}<strong>Join Zoom Meeting:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/j/95954655851?pwd=wDEdZgpixORb8u8884SOR3yJEPPuZV.1"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                >
                  https://big-go-id.zoom.us/j/95954655851?pwd=wDEdZgpixORb8u8884SOR3yJEPPuZV.1
                </a>
                {"\n\n"}<strong>Meeting ID:</strong> 959 5465 5851
                {"\n"}<strong>Passcode:</strong> M6=ecW%S
                {"\n\n"}<strong>Join instructions:</strong>{" "}
                <a
                  href="https://big-go-id.zoom.us/meetings/95954655851/invitations?signature=FE2pEw7EfBVxvneylhS6D-8Jpe-STaeAl1oGt_OWut4"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                >
                  https://big-go-id.zoom.us/meetings/95954655851/invitations?signature=FE2pEw7EfBVxvneylhS6D-8Jpe-STaeAl1oGt_OWut4
                </a>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Close</Button>
            </DialogActions>
          </Dialog>

        </Box>

        {/* Right side: Filter + Unified Announcement Table */}
        <Box flex={{ xs: "1", md: "0 0 40%" }}>
          <Typography
            variant="h3"
            component="h3"
            gutterBottom
            sx={{ fontSize: "1.2rem", fontWeight: "bold", mb: 2, color: "#2e2e2e" }}
          >
            LATEST ANNOUNCEMENTS
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="project-filter-label" sx={{ fontSize: "0.875rem" }}>
              Filter by Project
            </InputLabel>
            <Select
              labelId="project-filter-label"
              value={selectedFilter}
              label="Filter by Project"
              onChange={(e) => setSelectedFilter(e.target.value)}
              sx={{
                fontSize: "0.875rem",
                "& .MuiSelect-select": { padding: "12px 14px" },
              }}
            >
              <MenuItem value="all" sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip label="ALL" size="small" sx={{ backgroundColor: "rgba(46, 46, 46, 0.1)", color: "#2e2e2e", fontWeight: 600, fontSize: "0.7rem" }} />
                  All Projects
                </Box>
              </MenuItem>
              {Object.entries(projectConfigs).map(([key, config]) => (
                <MenuItem key={key} value={key} sx={{ fontSize: "0.875rem" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={key.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: config.bgColor,
                        color: config.color,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        border: `1px solid ${config.color}`,
                        minWidth: "45px",
                      }}
                    />
                    {config.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" sx={{ mb: 2, color: "#666", fontStyle: "italic" }}>
            {selectedFilter === "all"
              ? "Showing latest announcements from all 16 projects"
              : `Showing announcements from ${projectConfigs[selectedFilter]?.label}`}
          </Typography>
          <UnifiedAnnouncementTable
            allData={allAnnouncementsData}
            selectedFilter={selectedFilter}
          />
        </Box>
      </Box>
    </Box>
  );
}